import React from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { useFetchDeclarant } from '../Declarant/hooks/useFetchDeclarant';
import { isErpIntegrationAvailable } from 'modules/Declarant/config/erpIntegration';
import { useBranding } from 'modules/Common/hooks/useBranding';
import RouteAccessGate, { queryStatus } from 'modules/Common/permissions/RouteAccessGate';

// Carbon Radar Pages
import { RedirectToSignIn, SignedIn, SignedOut } from '@clerk/clerk-react';
import ComingSoonPage from 'modules/Common/layout/ComingSoonPage';
import ContextProvider from 'modules/App/contexts/ContextProvider';
import InitPage from './pages/InitPage';
import PaymentPage from './pages/PaymentPage';
import PricingPage from './pages/PricingPage';
import { CgToaster } from 'modules/Common/ui/toast';
import NotFoundPage from 'modules/Common/layout/NotFoundPage';
import { OrganizationType } from 'modules/Common/utils/Utility';
import UpgradePage from './pages/UpgradePage';
import TrialExpiredPage from './pages/TrialExpiredPage';

// Onboarding Pages
import OnboardingTypeSelection from '../Onboarding/OnboardingTypeSelection';
import OnboardingWizard from '../Onboarding/OnboardingWizard';
import { useIsFeatureLocked } from '../Common/config/lockedFeatures';
import PrivateRoute from './components/PrivateRoute';
import VerifierSetupGate from 'modules/Verifier/components/VerifierSetupGate';
import { EuRegulationGuard, UkRegulationGuard } from './components/RegulationGuards';
import SupplierApp from '../FreeSupplier/app/routes';
import SharePortalRouteGate from '../FreeSupplier/SharePortalRouteGate';
import { SUPPLIER_HOME_PATH, SupplierEntitlement } from '../FreeSupplier/planAccess';
import { SupplierRoutePath } from '../FreeSupplier/routes';
import WorkspacePlanBoundary from './components/WorkspacePlanBoundary';
import { withTabQuery } from 'modules/Common/entityDetails/tabQuery';
import { RECONCILIATION_SHOWN } from 'modules/Reconciliation/config/featureFlag';
import {
  AnalyticsOverviewRoute,
  BatchDetailPage,
  BenchmarksSearchPage,
  CarbonPriceTrackerPage,
  CbamCostsEuRoute,
  CbamCertificatesPage,
  ReconciliationRunPage,
  CbamHedgingPage,
  CbamCostsUkRoute,
  UkCostCalculatorPage,
  CbamPremiumsPage,
  CbamQuoteCreatePage,
  CbamQuotesSearchPage,
  CompaniesSearchPage,
  ConsultantOverviewPage,
  VerifierHomePage,
  VerifierSetupPage,
  EngagementPage,
  VerifierAuditPage,
  VerifierAuditPlanPage,
  VerifierTeamPage,
  VerifierFirmProfilePage,
  CostRadarPage,
  DeclarantOverviewPage,
  DiagramAuditRedirect,
  DiagramEditorPage,
  DownloadHubPage,
  ErpIntegrationPage,
  EmissionsDataPage,
  ImportCreatePage,
  ImportEditPage,
  ImportsSearchPage,
  InstallationDetails,
  InstallationsPage,
  LineEditPage,
  LineViewPage,
  OperatorAnalyticsPage,
  OperatorConnectedPage,
  OperatorInstallationDetailsPage,
  OperatorInstallationsPage,
  OperatorInvitePage,
  OperatorOverviewPage,
  OperatorReportPage,
  OperatorReportsPage,
  PlanVersionPage,
  PromotePurchasePage,
  PublicCatalogPage,
  PurchaseCreatePage,
  PurchaseEditPage,
  PurchaseLineEditPage,
  PurchaseLineViewPage,
  PurchasesSearchPage,
  ReportPage,
  RoleEditorPage,
  RolesListPage,
  SingleImportPage,
  SingleReportPage,
  StockDownloadHubPage,
  StockInstallationDetailPage,
  StockInventoryLotsPage,
  StockLotBreakdownPage,
  StockPurchaseOrdersPage,
  StockSalesOrdersPage,
  StockSupplierDetailPage,
  StockSuppliersPage,
  StockUploadHubPage,
  SupplierChasePage,
  SupplierDetailsPage,
  SuppliersSearchPage,
  TeamManagementPage,
  UkImportsSearchPage,
  UkRegistrationSummaryPage,
  UkThresholdBreakdownPage,
  UploadHubPage,
  UploadLogDetailPage,
  VerificationPage,
} from './config/routeComponents';

// `status="ready"` is deliberate and safe here, despite useIsFeatureLocked
// reading the permissions context (which answers false for everything while
// /permissions/me is in flight). isFeatureLocked() is true only when the user
// definitively HOLDS a locked-for role, so an unresolved payload can only ever
// produce "not locked" — this guard fails OPEN, never into a denial. There is
// therefore no pending state to model. (The brief fail-open window is
// pre-existing and self-correcting; see OI-8.)
const FeatureGuard = ({ featureKey, children }) => (
  <RouteAccessGate
    status="ready"
    allowed={!useIsFeatureLocked(featureKey)}
  >
    {children}
  </RouteAccessGate>
);

// Declarant feature-flag gate for the Companies area: only customs
// representatives (declarant.isCustomsRepresentative) manage third-party
// companies.
//
// All three outcomes are now distinct. Previously a FAILED GET /declarant left
// `declarant` undefined, so `!declarant?.isCustomsRepresentative` was true and
// the user was told "you do not have permission" for what was actually a
// network error — with no way to retry. That is exactly the conflation N6
// exists to prevent.
const CustomsRepGuard = ({ children }) => {
  const { data: declarant, isPending, isError, refetch } = useFetchDeclarant();

  return (
    <RouteAccessGate
      status={queryStatus({ isPending, isError })}
      allowed={Boolean(declarant?.isCustomsRepresentative)}
      // Retry must re-run THIS query. The default retries /permissions/me,
      // which would leave the failed GET /declarant exactly as it was and give
      // the user a button that does nothing.
      onRetry={refetch}
    >
      {children}
    </RouteAccessGate>
  );
};

const Routes = () => {
  const brandKey = useBranding((brand) => brand.key);
  if (!process.env.REACT_APP_CLERK_PUBLISHABLE_KEY) {
    throw new Error('Missing Publishable Key');
  }

  return (
    <>
      <SignedIn>
        <WorkspacePlanBoundary>
          <ContextProvider>
            <>
              <CgToaster />
              <Switch>
                {/* === Legacy route redirects === */}
                <Route
                  path="/suppliers/search"
                  render={(props) => <Redirect to="/suppliers" />}
                />
                <Route
                  path="/supplier/:id([0-9a-fA-F]{24})"
                  render={(props) => <Redirect to={`/suppliers/${props.match.params.id}`} />}
                />
                <Route
                  path="/inst/:id"
                  render={(props) => <Redirect to={`/installations/${props.match.params.id}`} />}
                />
                <Route
                  path="/imports/search"
                  render={(props) => <Redirect to="/imports" />}
                />
                <Route
                  path="/emissions/cbam-emissions"
                  render={(props) => <Redirect to="/emissions" />}
                />
                <Route
                  path="/analytics/overview"
                  render={(props) => <Redirect to="/analytics-overview" />}
                />
                <Route
                  path="/companies/search"
                  render={(props) => <Redirect to="/companies" />}
                />
                {/* UK routes moved under the /uk/* prefix (CG-2312). */}
                <Route
                  path="/uk-imports/threshold"
                  render={(props) => <Redirect to="/uk/imports/threshold" />}
                />
                <Route
                  path="/uk-imports"
                  render={(props) => <Redirect to="/uk/imports" />}
                />
                {/* Routes accessible by both */}
                <Route
                  path="/"
                  exact
                  render={() => <Redirect to="/init" />}
                />
                <Route
                  path="/init"
                  exact
                  component={InitPage}
                />
                <Route
                  path="/payment"
                  exact
                  component={PaymentPage}
                />
                <Route
                  path="/pricing"
                  exact
                  component={PricingPage}
                />
                <Route
                  path="/upgrade"
                  exact
                  component={UpgradePage}
                />
                <Route
                  path="/trial-expired"
                  exact
                  component={TrialExpiredPage}
                />

                {/* Onboarding routes - accessible after Clerk sign-in */}
                <Route
                  path="/onboarding/type-selection"
                  exact
                  component={OnboardingTypeSelection}
                />
                <Route
                  path="/onboarding/wizard"
                  exact
                  component={OnboardingWizard}
                />

                {/* Declarant-only routes */}
                <PrivateRoute
                  path="/overview"
                  component={DeclarantOverviewPage}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="declarant_home"
                />
                <PrivateRoute
                  path="/dashboard"
                  component={ComingSoonPage}
                  allowedTypes={[OrganizationType.Declarant]}
                />

                {/* CBAM Quotes Routes — both regimes. The pages read the
                    declarant's regime for years, goods, price and currency
                    (backend docs/features/uk-quoting-and-quotes). */}
                <PrivateRoute
                  path="/cbam-quotes/create"
                  render={(props) => <CbamQuoteCreatePage {...props} />}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="cbam_quotes"
                />
                <PrivateRoute
                  path="/cbam-quotes/:id/edit"
                  render={(props) => <CbamQuoteCreatePage {...props} />}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="cbam_quotes"
                />
                <PrivateRoute
                  path="/cbam-quotes/:id"
                  render={(props) => <CbamQuoteCreatePage {...props} />}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="cbam_quotes"
                />
                <PrivateRoute
                  path="/cbam-quotes"
                  render={(props) => <CbamQuotesSearchPage {...props} />}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="cbam_quotes"
                />

                <PrivateRoute
                  path="/suppliers/chase"
                  component={SupplierChasePage}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="suppliers"
                />
                <PrivateRoute
                  path="/suppliers/create"
                  component={SuppliersSearchPage}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="suppliers"
                />

                <PrivateRoute
                  path="/suppliers/edit/:id"
                  component={SuppliersSearchPage}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="suppliers"
                />
                {/* The supplier-engagement preview addresses (phases 1–6) and the old
                    placeholder pages — before /suppliers/:id so neither is read as an id. */}
                <Route
                  exact
                  path="/suppliers/engagement/chase"
                  render={() => <Redirect to="/suppliers/chase" />}
                />
                <Route
                  exact
                  path="/suppliers/engagement/:id"
                  render={(props) => <Redirect to={`/suppliers/${props.match.params.id}`} />}
                />
                <Route
                  exact
                  path="/suppliers/engagement"
                  render={() => <Redirect to="/suppliers" />}
                />
                <Route
                  exact
                  path="/suppliers/sent-invitations"
                  render={() => <Redirect to="/suppliers?segment=awaiting" />}
                />
                <Route
                  exact
                  path="/suppliers/accepted-invitations"
                  render={() => <Redirect to="/suppliers?segment=connected" />}
                />
                <PrivateRoute
                  path="/suppliers/:id"
                  component={SupplierDetailsPage}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="suppliers"
                />

                <PrivateRoute
                  path="/installations/create"
                  component={InstallationsPage}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="installations"
                />
                <PrivateRoute
                  path="/installations/edit/:id"
                  component={InstallationsPage}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="installations"
                />
                <PrivateRoute
                  path="/installations/:id"
                  exact={false}
                  component={InstallationDetails}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="installations"
                />
                <PrivateRoute
                  path="/installations"
                  component={InstallationsPage}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="installations"
                />

                <PrivateRoute
                  path="/suppliers"
                  component={SuppliersSearchPage}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="suppliers"
                />

                <PrivateRoute
                  path="/imports/search"
                  component={ImportsSearchPage}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="imports"
                />
                <PrivateRoute
                  path="/installations"
                  component={InstallationsPage}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="installations"
                />
                <PrivateRoute
                  path="/imports/create"
                  component={ImportCreatePage}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="imports"
                />
                {/* A purchase file's run (phase 12): the same page, under Imports — the
                    flow the Imports page offers — so the sidebar marks Imports. Before
                    the two-segment line routes, which would otherwise match it. */}
                <PrivateRoute
                  path="/imports/reconciliation/:runId"
                  render={(props) =>
                    RECONCILIATION_SHOWN ? (
                      <ReconciliationRunPage {...props} />
                    ) : (
                      <Redirect to="/imports" />
                    )
                  }
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="imports"
                />
                {/* Promote a purchase (docs/features/purchases PU7) — before the
                    two-segment line routes, which would otherwise match it. */}
                <PrivateRoute
                  path="/imports/promote/:purchaseId"
                  component={PromotePurchasePage}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="imports"
                />
                <PrivateRoute
                  path="/imports/:shipmentId/grouped"
                  component={ImportEditPage}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="imports"
                />
                <PrivateRoute
                  path="/imports/:shipmentId/:contentId/edit"
                  component={LineEditPage}
                  allowedTypes={[OrganizationType.Declarant]}
                />
                <PrivateRoute
                  path="/imports/:shipmentId/:contentId"
                  component={LineViewPage}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="imports"
                />
                <PrivateRoute
                  path="/imports/:id"
                  component={SingleImportPage}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="imports"
                />
                <PrivateRoute
                  path="/imports"
                  render={(props) => (
                    <EuRegulationGuard>
                      <ImportsSearchPage {...props} />
                    </EuRegulationGuard>
                  )}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="imports"
                />
                {/* Purchases (docs/features/purchases): the Imports pages under the
                    purchase descriptor. One route for both regimes — the UK
                    imports list is separate for its customs-only surfaces
                    (threshold, HMRC), none of which a purchase has. */}
                {/* A customs record file (docs/features/imports-purchases-reconciliation R11):
                    before the purchase id routes, which would read
                    "reconciliation" as a purchase id. No list
                    page: Upload hub history is the way back to a file (R37). */}
                <PrivateRoute
                  path="/purchases/reconciliation/:runId"
                  render={(props) =>
                    RECONCILIATION_SHOWN ? (
                      <ReconciliationRunPage {...props} />
                    ) : (
                      <Redirect to="/purchases" />
                    )
                  }
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="declarant_purchases"
                />
                <PrivateRoute
                  path="/purchases/create"
                  component={PurchaseCreatePage}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="declarant_purchases"
                />
                <PrivateRoute
                  path="/purchases/:shipmentId/grouped"
                  component={PurchaseEditPage}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="declarant_purchases"
                />
                <PrivateRoute
                  path="/purchases/:shipmentId/:contentId/edit"
                  component={PurchaseLineEditPage}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="declarant_purchases"
                />
                <PrivateRoute
                  path="/purchases/:shipmentId/:contentId"
                  component={PurchaseLineViewPage}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="declarant_purchases"
                />
                <PrivateRoute
                  path="/purchases"
                  component={PurchasesSearchPage}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="declarant_purchases"
                />
                {/* More specific paths first — Switch takes the first match. */}
                <PrivateRoute
                  path="/uk/imports/registration"
                  render={(props) => (
                    <UkRegulationGuard>
                      <UkRegistrationSummaryPage {...props} />
                    </UkRegulationGuard>
                  )}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="imports"
                />
                <PrivateRoute
                  path="/uk/imports/threshold"
                  render={(props) => (
                    <UkRegulationGuard>
                      <UkThresholdBreakdownPage {...props} />
                    </UkRegulationGuard>
                  )}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="imports"
                />
                <PrivateRoute
                  path="/uk/imports"
                  render={(props) => (
                    <UkRegulationGuard>
                      <UkImportsSearchPage {...props} />
                    </UkRegulationGuard>
                  )}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="imports"
                />

                <PrivateRoute
                  path="/upload-hub/batch/:id"
                  component={BatchDetailPage}
                  allowedTypes={[OrganizationType.Declarant, OrganizationType.Operator]}
                  resourceKey={{
                    [OrganizationType.Declarant]: 'declarant_upload_hub',
                    [OrganizationType.Operator]: 'operator_upload_hub',
                  }}
                />

                <PrivateRoute
                  path="/upload-hub/upload/:id"
                  component={UploadLogDetailPage}
                  allowedTypes={[OrganizationType.Declarant, OrganizationType.Operator]}
                  resourceKey={{
                    [OrganizationType.Declarant]: 'declarant_upload_hub',
                    [OrganizationType.Operator]: 'operator_upload_hub',
                  }}
                />

                <PrivateRoute
                  path="/upload-hub"
                  component={UploadHubPage}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="declarant_upload_hub"
                />

                <PrivateRoute
                  path="/download-hub"
                  component={DownloadHubPage}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="declarant_download_hub"
                />

                {/* Main instance + allowlisted white-labels only (backend
                    docs/features/erp-integration-requests D2); elsewhere the
                    route does not exist. Any declarant member may use it (D4). */}
                {isErpIntegrationAvailable(brandKey) ? (
                  <PrivateRoute
                    path="/erp-integration"
                    component={ErpIntegrationPage}
                    allowedTypes={[OrganizationType.Declarant]}
                  />
                ) : null}

                <PrivateRoute
                  path="/emissions"
                  component={EmissionsDataPage}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="emissions"
                />

                <Route
                  path="/supplier-chase"
                  render={() => <Redirect to="/suppliers/chase" />}
                />

                <PrivateRoute
                  path="/benchmarks"
                  render={(props) => (
                    <EuRegulationGuard>
                      <BenchmarksSearchPage {...props} />
                    </EuRegulationGuard>
                  )}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="benchmarks"
                />

                {/* Price risk & hedging, a sub-page of Costs (cbam-hedging-page H1): before
                    /cbam-costs, which matches it as a prefix. Same guard and permission. */}
                <PrivateRoute
                  path="/cbam-costs/hedging"
                  render={() => (
                    <EuRegulationGuard ukRedirect="/uk/costs">
                      <CbamHedgingPage />
                    </EuRegulationGuard>
                  )}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="cbam_costs"
                />
                {/* EU Costs (and its Group view) is EU-regime only: a UK declarant is
                    sent to its own UK Costs page (uk-cbam-analytics D13). */}
                <PrivateRoute
                  path="/cbam-costs"
                  render={() => (
                    <EuRegulationGuard ukRedirect="/uk/costs">
                      <CbamCostsEuRoute />
                    </EuRegulationGuard>
                  )}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="cbam_costs"
                />
                {/* Certificates: the account beside the Costs bill, EU only, and on
                    the Costs permission (certificate-management D1, D2). */}
                <PrivateRoute
                  path="/cbam-certificates"
                  render={() => (
                    <EuRegulationGuard ukRedirect="/uk/costs">
                      <CbamCertificatesPage />
                    </EuRegulationGuard>
                  )}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="cbam_costs"
                />
                {/* Same page, same table, a different regime — the ROUTE is
                    what states which, and importing the regime module is what
                    registers it (uk-cbam-cost-breakdown D16). Guarded like
                    /uk/imports, and permissioned on the same `cbam_costs`
                    resource: it is the same product surface for a UK org. */}
                <PrivateRoute
                  path="/uk/costs"
                  render={() => (
                    <UkRegulationGuard>
                      <CbamCostsUkRoute />
                    </UkRegulationGuard>
                  )}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="cbam_costs"
                />
                {/* The UK cost calculator (uk-cost-calculator-widget phase 4):
                    the public embed's calculator as a declarant page, guarded
                    and permissioned like /uk/costs — it is the what-if beside
                    the bill. */}
                <PrivateRoute
                  exact
                  path="/uk/cost-calculator"
                  render={() => (
                    <UkRegulationGuard>
                      <UkCostCalculatorPage />
                    </UkRegulationGuard>
                  )}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="cbam_costs"
                />
                {/* One URL, two regimes (uk-cbam-reports R1/R2): the route is
                    unchanged and AnalyticsOverviewRoute picks between the EU
                    dashboard and a UK stub. Not a guard + redirect — the nav
                    item exists in both regimes, only its destination differs. */}
                <PrivateRoute
                  path="/analytics-overview"
                  component={AnalyticsOverviewRoute}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="declarant_overview"
                />
                {/* EU-only, and now enforced rather than merely hidden (D5).
                    The sidebar has always kept this item out of the UK
                    declarant menu, but a hidden entry is not an unreachable
                    route. Default Costs prices in EEX euros against an EU
                    phase-in factor; the quoting half has its own route below,
                    which is where a UK declarant is sent. Operators and consultants pass through:
                    the guard only redirects a declarant explicitly on UK. */}
                <PrivateRoute
                  path="/cost-radar"
                  render={(props) => (
                    // A UK declarant's Cost radar bookmark lands on the part of
                    // it UK can use: the Quoting Map (uk-quoting-and-quotes D1).
                    <EuRegulationGuard ukRedirect="/quoting-map">
                      <CostRadarPage {...props} />
                    </EuRegulationGuard>
                  )}
                  allowedTypes={[
                    OrganizationType.Declarant,
                    OrganizationType.Operator,
                    OrganizationType.Consultant,
                  ]}
                  resourceKey={{
                    [OrganizationType.Declarant]: 'declarant_cost_radar',
                    [OrganizationType.Operator]: 'operator_cost_radar',
                  }}
                />
                {/* The Quoting Map's own route, open to both regimes
                    (uk-quoting-and-quotes D1). It is the same page as
                    /cost-radar with quoting mode pinned, so Default Costs —
                    EU-only — is never reachable from here. It keeps
                    /cost-radar's permission gating (D7, quoting-map-procurement
                    Q28) and the org types quoting mode serves: operators have
                    no quoting mode. EU users still reach the map as Cost
                    radar's quoting mode; their navigation is unchanged. */}
                <PrivateRoute
                  exact
                  path="/quoting-map"
                  render={(props) => (
                    <CostRadarPage
                      {...props}
                      quotingOnly
                    />
                  )}
                  allowedTypes={[OrganizationType.Declarant, OrganizationType.Consultant]}
                  resourceKey={{ [OrganizationType.Declarant]: 'declarant_cost_radar' }}
                />
                <PrivateRoute
                  path="/companies"
                  render={(props) => (
                    <CustomsRepGuard>
                      <CompaniesSearchPage {...props} />
                    </CustomsRepGuard>
                  )}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="companies"
                />
                <PrivateRoute
                  path="/reports/:id"
                  component={SingleReportPage}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="declarant_reports"
                />
                <PrivateRoute
                  path="/reports"
                  component={ReportPage}
                  allowedTypes={[OrganizationType.Declarant]}
                  resourceKey="declarant_reports"
                />

                {/* Operator-only routes */}
                {/* Portal management remains available after a free supplier upgrades:
                  the canonical CbamPortal keeps its supplier-revision source and consent
                  settings until the operator explicitly changes them.

                  OI-10: these stay mounted for every Operator org, but only orgs on
                  the free-tier Journey entitlement author here — a paying operator is
                  redirected to their own studio at /public-catalog so there is one
                  authoring UI per operator, not two over the same record. See
                  SharePortalRouteGate. */}
                <PrivateRoute
                  path={[SupplierRoutePath.Share, SupplierRoutePath.Embed]}
                  exact
                  component={SharePortalRouteGate}
                  allowedTypes={[OrganizationType.Operator]}
                />
                {/* Free-tier supplier journey. The entire /supplier/* subtree is
                  one app — LiteShell + a live-data provider with its own internal
                  router (see FreeSupplier/app/routes.jsx). It renders outside the
                  heavy AppLayout via isSupplierHomePath in _app.jsx. Free-tier
                  suppliers are Operator-type orgs, gated to the Journey
                  entitlement. */}
                <PrivateRoute
                  path={SUPPLIER_HOME_PATH}
                  exact={false}
                  component={SupplierApp}
                  allowedTypes={[OrganizationType.Operator]}
                  requiredEntitlement={SupplierEntitlement.Journey}
                />
                <PrivateRoute
                  path="/operator-overview"
                  component={OperatorOverviewPage}
                  allowedTypes={[OrganizationType.Operator]}
                  resourceKey="operator_home"
                />
                {/* Retired audit console (verifier-firm-foundation phase 5 §2):
                  redirects to the installation's Verification tab, which now
                  holds the findings. Must precede /diagrams/:id (exact={false})
                  or the editor swallows the subpath. */}
                <PrivateRoute
                  path="/diagrams/:id/audit"
                  exact
                  component={DiagramAuditRedirect}
                  allowedTypes={[OrganizationType.Operator, OrganizationType.Consultant]}
                />
                {/* Per-report editor: must come before /diagrams/:id (which
                  uses exact={false}) so this exact subpath wins the match. */}
                <PrivateRoute
                  path="/diagrams/:id/operator-reports/:reportId"
                  exact
                  component={OperatorReportPage}
                  allowedTypes={[OrganizationType.Operator, OrganizationType.Consultant]}
                />
                <PrivateRoute
                  path="/diagrams/:id/operator-reports/:reportId/versions/:versionId"
                  exact
                  component={PlanVersionPage}
                  allowedTypes={[OrganizationType.Operator, OrganizationType.Consultant]}
                />
                <PrivateRoute
                  path={['/new-diagram', '/diagrams/:id']}
                  exact={false}
                  render={(props) => (
                    <FeatureGuard featureKey="operator-diagrams">
                      <DiagramEditorPage {...props} />
                    </FeatureGuard>
                  )}
                  allowedTypes={[
                    OrganizationType.Operator,
                    OrganizationType.Consultant,
                    OrganizationType.Declarant,
                  ]}
                />
                {/* Legacy route redirects for operator installations */}
                <Route
                  path="/operator-installations/:id/view"
                  render={(props) => (
                    <Redirect to={`/operator-installations/${props.match.params.id}`} />
                  )}
                />
                <Route
                  path="/operator-installations/:id/emissions"
                  render={(props) => (
                    <Redirect
                      to={withTabQuery(
                        `/operator-installations/${props.match.params.id}`,
                        'input-sources',
                      )}
                    />
                  )}
                />
                {/* The standalone diagrams page was retired in parity phase 4 —
                    diagrams are rows in the Input sources tab. */}
                <Route
                  path="/operator-installations/:id/diagrams"
                  render={(props) => (
                    <Redirect
                      to={withTabQuery(
                        `/operator-installations/${props.match.params.id}`,
                        'input-sources',
                      )}
                    />
                  )}
                />
                {/* Edit moved to a modal triggered from the detail page —
                  redirect any stale /edit links so bookmarks still land
                  somewhere useful. */}
                <Route
                  path="/operator-installations/:id/edit"
                  render={(props) => (
                    <Redirect to={`/operator-installations/${props.match.params.id}`} />
                  )}
                />
                <PrivateRoute
                  path="/operator-installations/:id"
                  component={OperatorInstallationDetailsPage}
                  allowedTypes={[OrganizationType.Operator, OrganizationType.Consultant]}
                  resourceKey={{ [OrganizationType.Operator]: 'operator_installations' }}
                />
                <PrivateRoute
                  path="/operator-installations"
                  component={OperatorInstallationsPage}
                  allowedTypes={[OrganizationType.Operator, OrganizationType.Consultant]}
                  resourceKey={{ [OrganizationType.Operator]: 'operator_installations' }}
                />

                <PrivateRoute
                  path="/operator-table"
                  component={() => <Redirect to="/operator-installations" />}
                  allowedTypes={[OrganizationType.Operator]}
                />
                <PrivateRoute
                  path="/connections/connected"
                  component={OperatorConnectedPage}
                  allowedTypes={[OrganizationType.Operator]}
                  resourceKey="connections"
                />
                <PrivateRoute
                  path="/connections/invites"
                  component={OperatorInvitePage}
                  allowedTypes={[OrganizationType.Operator]}
                  resourceKey="connection_requests"
                />
                <PrivateRoute
                  path="/team/manage"
                  render={(props) => (
                    <FeatureGuard featureKey="team-management">
                      <TeamManagementPage {...props} />
                    </FeatureGuard>
                  )}
                  allowedTypes={[OrganizationType.Operator]}
                  adminOnly
                />
                <PrivateRoute
                  path="/operator-analytics"
                  component={OperatorAnalyticsPage}
                  allowedTypes={[OrganizationType.Operator]}
                  resourceKey="operator_overview"
                />
                <PrivateRoute
                  path="/operator-premiums"
                  component={CbamPremiumsPage}
                  allowedTypes={[OrganizationType.Operator]}
                  resourceKey="operator_premiums"
                />
                <PrivateRoute
                  path="/public-catalog"
                  component={PublicCatalogPage}
                  allowedTypes={[OrganizationType.Operator]}
                />

                {/* Stock routes */}
                <PrivateRoute
                  path="/stock/upload-hub"
                  component={StockUploadHubPage}
                  allowedTypes={[OrganizationType.Operator]}
                  resourceKey="operator_upload_hub"
                />
                <PrivateRoute
                  path="/stock/download-hub"
                  component={StockDownloadHubPage}
                  allowedTypes={[OrganizationType.Operator]}
                  resourceKey="operator_download_hub"
                />
                <PrivateRoute
                  path="/stock/suppliers/:id"
                  component={StockSupplierDetailPage}
                  allowedTypes={[OrganizationType.Operator]}
                  resourceKey="operator_suppliers"
                />
                <PrivateRoute
                  path="/stock/suppliers"
                  component={StockSuppliersPage}
                  allowedTypes={[OrganizationType.Operator]}
                  resourceKey="operator_suppliers"
                />
                {/* Retired sub-page (parity phase 4) — the detail page's Final
                    emissions tab is the same content inside the EntityDetails
                    shell, so bookmarks land there. */}
                <Route
                  path="/stock/installations/:id/emissions"
                  render={(props) => (
                    <Redirect
                      to={withTabQuery(
                        `/stock/installations/${props.match.params.id}`,
                        'final-emissions',
                      )}
                    />
                  )}
                />
                <PrivateRoute
                  path="/stock/installations/:id"
                  component={StockInstallationDetailPage}
                  allowedTypes={[OrganizationType.Operator]}
                />
                <PrivateRoute
                  path="/stock/purchases"
                  component={StockPurchaseOrdersPage}
                  allowedTypes={[OrganizationType.Operator]}
                  resourceKey="purchases"
                />
                <PrivateRoute
                  path="/stock/purchase-orders"
                  component={() => <Redirect to="/stock/purchases" />}
                  allowedTypes={[OrganizationType.Operator]}
                  resourceKey="purchases"
                />
                <PrivateRoute
                  path="/inventory/lots/:lotNumber"
                  component={StockLotBreakdownPage}
                  allowedTypes={[OrganizationType.Operator]}
                  resourceKey="inventory"
                />
                <PrivateRoute
                  path="/inventory/lots"
                  component={StockInventoryLotsPage}
                  allowedTypes={[OrganizationType.Operator]}
                  resourceKey="inventory"
                />
                <PrivateRoute
                  path="/stock/sales"
                  component={StockSalesOrdersPage}
                  allowedTypes={[OrganizationType.Operator]}
                  resourceKey="sell_orders"
                />
                <PrivateRoute
                  path="/sales-orders"
                  component={() => <Redirect to="/stock/sales" />}
                  allowedTypes={[OrganizationType.Operator]}
                  resourceKey="sell_orders"
                />
                <PrivateRoute
                  path="/stock/reports"
                  component={OperatorReportsPage}
                  allowedTypes={[OrganizationType.Operator]}
                  resourceKey="operator_reports"
                />

                {/* Exports is deprecated — superseded by the stock sales flow */}
                <PrivateRoute
                  path="/exports"
                  component={() => <Redirect to="/stock/sales" />}
                  allowedTypes={[OrganizationType.Operator]}
                  resourceKey="sell_orders"
                />

                {/* Operator verification page */}
                <PrivateRoute
                  path="/verification"
                  component={VerificationPage}
                  allowedTypes={[OrganizationType.Operator]}
                  resourceKey="verification"
                />

                {/* Carbon Price Tracker - Available to all user types */}
                <PrivateRoute
                  path="/carbon-price-tracker"
                  render={(props) => (
                    <EuRegulationGuard>
                      <CarbonPriceTrackerPage {...props} />
                    </EuRegulationGuard>
                  )}
                  allowedTypes={[
                    OrganizationType.Declarant,
                    OrganizationType.Operator,
                    OrganizationType.Consultant,
                  ]}
                  resourceKey={{
                    [OrganizationType.Declarant]: 'declarant_certificate_price',
                    [OrganizationType.Operator]: 'operator_certificate_price',
                  }}
                />

                {/* Consultant routes */}
                <PrivateRoute
                  path="/consultant-overview"
                  component={ConsultantOverviewPage}
                  allowedTypes={[OrganizationType.Consultant]}
                />

                {/* Verifier firm (verifier-firm-foundation). Every page but setup sits behind the
                    setup gate (VF22): an Admin finishes the firm's §1.3 details first.
                    MOST SPECIFIC FIRST: <Switch> matches on the element's own props, and
                    PrivateRoute's `exact` default is invisible to it — so `/verifier` must come
                    last or it swallows every route below it (and loops the setup redirect). */}
                <PrivateRoute
                  path="/verifier/setup"
                  component={VerifierSetupPage}
                  allowedTypes={[OrganizationType.Verifier]}
                />
                <PrivateRoute
                  path="/verifier/team"
                  render={() => (
                    <VerifierSetupGate>
                      <VerifierTeamPage />
                    </VerifierSetupGate>
                  )}
                  allowedTypes={[OrganizationType.Verifier]}
                />
                <PrivateRoute
                  path="/verifier/firm"
                  render={() => (
                    <VerifierSetupGate>
                      <VerifierFirmProfilePage />
                    </VerifierSetupGate>
                  )}
                  allowedTypes={[OrganizationType.Verifier]}
                />
                <PrivateRoute
                  path="/verifier/engagements/:engagementId/diagrams/:diagramId/plan"
                  render={() => (
                    <VerifierSetupGate>
                      <VerifierAuditPlanPage />
                    </VerifierSetupGate>
                  )}
                  allowedTypes={[OrganizationType.Verifier]}
                />
                <PrivateRoute
                  path="/verifier/engagements/:engagementId/diagrams/:diagramId"
                  render={() => (
                    <VerifierSetupGate>
                      <VerifierAuditPage />
                    </VerifierSetupGate>
                  )}
                  allowedTypes={[OrganizationType.Verifier]}
                />
                <PrivateRoute
                  path="/verifier/engagements/:engagementId"
                  render={() => (
                    <VerifierSetupGate>
                      <EngagementPage />
                    </VerifierSetupGate>
                  )}
                  allowedTypes={[OrganizationType.Verifier]}
                />
                <PrivateRoute
                  path="/verifier"
                  render={() => (
                    <VerifierSetupGate>
                      <VerifierHomePage />
                    </VerifierSetupGate>
                  )}
                  allowedTypes={[OrganizationType.Verifier]}
                />

                {/* Roles & Permissions admin — gated on Clerk admin role.
                  Allowed for both declarant and operator orgs; consultants
                  don't manage roles in v1. */}
                <PrivateRoute
                  path="/settings/permissions/roles/:slug"
                  component={RoleEditorPage}
                  allowedTypes={[OrganizationType.Declarant, OrganizationType.Operator]}
                  adminOnly
                />
                <PrivateRoute
                  path="/settings/permissions/roles"
                  component={RolesListPage}
                  allowedTypes={[OrganizationType.Declarant, OrganizationType.Operator]}
                  adminOnly
                />

                {/* Catch-all for 404 - Must be the last route */}
                <Route
                  path="*"
                  component={NotFoundPage}
                />
              </Switch>
            </>
          </ContextProvider>
        </WorkspacePlanBoundary>
      </SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  );
};

export default Routes;
